import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { db } from '../services/firebase';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';

const MusicContext = createContext();

export function MusicProvider({ children }) {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [playlist, setPlaylist] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [mainTrack, setMainTrack] = useState(null);
  const [hasAutoPlayed, setHasAutoPlayed] = useState(false);

  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const dataArrayRef = useRef(null);

  // Fetch main track on mount
  useEffect(() => {
    const fetchMainTrack = async () => {
      if (!db) return;

      try {
        const q = query(
          collection(db, 'music'),
          where('isMainTrack', '==', true),
          limit(1)
        );
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const trackData = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
          setMainTrack(trackData);
        }
      } catch (error) {
        console.error('Error fetching main track:', error);
      }
    };

    fetchMainTrack();
  }, []);

  // Store playTrack in a ref so we can use it in the effect without dependency issues
  const playTrackRef = useRef(null);

  // Auto-play main track on first user interaction
  useEffect(() => {
    if (!mainTrack || hasAutoPlayed) return;

    const handleFirstInteraction = () => {
      if (mainTrack.audioUrl && !hasAutoPlayed && playTrackRef.current) {
        playTrackRef.current(mainTrack);
        setHasAutoPlayed(true);
      }
      // Remove listeners after first interaction
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };

    // Add listeners for first user interaction (required by browsers)
    document.addEventListener('click', handleFirstInteraction);
    document.addEventListener('keydown', handleFirstInteraction);
    document.addEventListener('touchstart', handleFirstInteraction);

    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };
  }, [mainTrack, hasAutoPlayed]);

  // Initialize audio element
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.crossOrigin = 'anonymous'; // Required for AudioContext with CORS
      audioRef.current.volume = 0.3; // Start at 30% volume
      audioRef.current.addEventListener('timeupdate', handleTimeUpdate);
      audioRef.current.addEventListener('loadedmetadata', handleLoadedMetadata);
      audioRef.current.addEventListener('ended', handleEnded);
      audioRef.current.addEventListener('play', () => setIsPlaying(true));
      audioRef.current.addEventListener('pause', () => setIsPlaying(false));
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeEventListener('timeupdate', handleTimeUpdate);
        audioRef.current.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audioRef.current.removeEventListener('ended', handleEnded);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update audio source when track changes
  useEffect(() => {
    if (audioRef.current && currentTrack?.audioUrl) {
      audioRef.current.src = currentTrack.audioUrl;
      audioRef.current.load();
      if (isPlaying) {
        audioRef.current.play().catch(console.error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrack?.audioUrl]);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    // Play next track if in playlist
    const currentIndex = playlist.findIndex(t => t.id === currentTrack?.id);
    if (currentIndex < playlist.length - 1) {
      setCurrentTrack(playlist[currentIndex + 1]);
    } else {
      setIsPlaying(false);
    }
  };

  const playTrack = useCallback((track, tracks = null) => {
    if (tracks) {
      setPlaylist(tracks);
    } else if (playlist.length === 0 || !playlist.find(t => t.id === track.id)) {
      setPlaylist([track]);
    }

    setCurrentTrack(track);
    setIsVisible(true);
    setIsPlaying(true);

    if (audioRef.current) {
      // Resume audio context if suspended (for browsers that require user gesture)
      if (audioContextRef.current?.state === 'suspended') {
        audioContextRef.current.resume();
      }

      // Set crossOrigin to allow CORS audio from Cloudinary
      audioRef.current.crossOrigin = 'anonymous';

      if (audioRef.current.src !== track.audioUrl) {
        audioRef.current.src = track.audioUrl;
        audioRef.current.load();
      }
      audioRef.current.play().catch((err) => {
        console.error('Play error:', err);
      });
    }
  }, [playlist]);

  // Keep playTrackRef updated
  useEffect(() => {
    playTrackRef.current = playTrack;
  }, [playTrack]);

  const pauseTrack = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setIsPlaying(false);
  }, []);

  const togglePlay = useCallback(() => {
    if (isPlaying) {
      pauseTrack();
    } else if (currentTrack) {
      // Resume audio context if it exists and is suspended
      if (audioContextRef.current?.state === 'suspended') {
        audioContextRef.current.resume().catch(console.error);
      }
      if (audioRef.current) {
        audioRef.current.play().catch(console.error);
      }
      setIsPlaying(true);
    }
  }, [isPlaying, currentTrack, pauseTrack]);

  const skipTrack = useCallback((direction) => {
    const currentIndex = playlist.findIndex(t => t.id === currentTrack?.id);
    let newIndex = currentIndex + direction;

    if (newIndex < 0) newIndex = playlist.length - 1;
    if (newIndex >= playlist.length) newIndex = 0;

    if (playlist[newIndex]) {
      setCurrentTrack(playlist[newIndex]);
      setIsPlaying(true);
      if (audioRef.current) {
        audioRef.current.src = playlist[newIndex].audioUrl;
        audioRef.current.load();
        audioRef.current.play().catch(console.error);
      }
    }
  }, [playlist, currentTrack]);

  const seekTo = useCallback((time) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }
  }, []);

  const closePlayer = useCallback(() => {
    pauseTrack();
    setIsVisible(false);
    setCurrentTrack(null);
  }, [pauseTrack]);

  // Initialize audio context for visualizer
  const initAudioContext = useCallback(() => {
    if (sourceRef.current || !audioRef.current) return; // Already initialized

    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;

      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext();
      }

      // Resume if suspended
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().catch(console.error);
      }

      analyserRef.current = audioContextRef.current.createAnalyser();

      // Create media element source - this routes audio through AudioContext
      sourceRef.current = audioContextRef.current.createMediaElementSource(audioRef.current);
      sourceRef.current.connect(analyserRef.current);
      analyserRef.current.connect(audioContextRef.current.destination);

      analyserRef.current.fftSize = 64;
      const bufferLength = analyserRef.current.frequencyBinCount;
      dataArrayRef.current = new Uint8Array(bufferLength);

    } catch (e) {
      console.warn('Audio visualizer initialization failed:', e.message);
    }
  }, []);

  const value = {
    currentTrack,
    playlist,
    isPlaying,
    isVisible,
    currentTime,
    duration,
    mainTrack,
    audioRef,
    audioContextRef,
    analyserRef,
    dataArrayRef,
    playTrack,
    pauseTrack,
    togglePlay,
    skipTrack,
    seekTo,
    closePlayer,
    setIsVisible,
    initAudioContext,
  };

  return (
    <MusicContext.Provider value={value}>
      {children}
    </MusicContext.Provider>
  );
}

export function useMusic() {
  const context = useContext(MusicContext);
  if (!context) {
    throw new Error('useMusic must be used within a MusicProvider');
  }
  return context;
}
